import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { usePurchaseDaily } from '../hooks/usePurchaseAnalysis.js';
import type { Granularity } from '../lib/kpi-aggregation.js';
import { GRANULARITY_OPTIONS } from '../lib/kpi-aggregation.js';
import { aggregatePurchaseRecords } from '../lib/purchase-aggregation.js';
import { ChartFilterBar } from '../components/chart/ChartFilterBar.js';
import { DateRangeFilter } from '../components/chart/DateRangeFilter.js';
import { PurchaseKpiCards } from '../components/purchase-analysis/PurchaseKpiCards.js';
import { PurchaseTrendCharts } from '../components/purchase-analysis/PurchaseTrendCharts.js';

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 購入分析ページ。購入率・購入単価・購入着数・購入売上の推移を粒度切替で可視化する。
 * @graph-connects usePurchaseDaily [calls] 購入分析日次データ取得
 */
export default function PurchaseAnalysisPage() {
  const { t } = useTranslation();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const { data, isLoading, error } = usePurchaseDaily(from || undefined, to || undefined);
  const [granularity, setGranularity] = useState<Granularity>('monthly');

  const chartData = useMemo(
    () => aggregatePurchaseRecords(data?.rows ?? [], granularity),
    [data?.rows, granularity],
  );

  if (isLoading) {
    return (
      <div className="mx-auto flex min-h-[400px] w-full max-w-[1400px] items-center justify-center px-4 py-8">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--color-accent-primary)]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto w-full max-w-[1400px] px-4 py-8">
        <Card className="border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950">
          <p className="text-red-800 dark:text-red-200">
            {t('common.fetchError')}: {error instanceof Error ? error.message : String(error)}
          </p>
        </Card>
      </div>
    );
  }

  const snapshotAt = data?.snapshotAt;

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-8">
      <p className="font-mono text-xs uppercase tracking-widest text-[var(--color-accent-primary)]">
        Finance
      </p>
      <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
        {t('purchaseAnalysis.title')}
      </h1>
      <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
        {t('purchaseAnalysis.description')}
      </p>

      <Card className="mt-6 flex flex-wrap items-center gap-3 p-3">
        <DateRangeFilter from={from} to={to} onFromChange={setFrom} onToChange={setTo} />
        <ChartFilterBar
          label={t('common.granularity')}
          options={GRANULARITY_OPTIONS}
          value={granularity}
          onChange={setGranularity}
        />
        {snapshotAt && (
          <span className="ml-auto text-xs text-[var(--color-text-muted)]">
            {t('common.lastUpdated')}:{' '}
            {new Date(snapshotAt).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })}
          </span>
        )}
      </Card>

      {chartData.length === 0 ? (
        <Card className="mt-6 p-8 text-center">
          <p className="text-[var(--color-text-muted)]">{t('purchaseAnalysis.noData')}</p>
        </Card>
      ) : (
        <div className="mt-6 space-y-6">
          <PurchaseKpiCards data={chartData} />
          <PurchaseTrendCharts data={chartData} />
        </div>
      )}
    </div>
  );
}
